import React, { useState, useEffect, useRef } from 'react';
import { Search, Check, Calendar, Truck, Package, X, ChevronDown } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { usePermissions } from '../../hooks/usePermissions';
import Button from '../../components/ui/Button';
import Input from '../../components/forms/Input';
import Modal from '../../components/ui/Modal';
import OrdenCompraDetalle from './OrdenCompraDetalle';
import apiService from '../../services/apiService';
import { formatMoney, formatDate } from '../../utils/formatters';
import './CuentasPorPagarPage.css';

const CuentasPorPagarPage = () => {
    const { loading, setLoading, showToast, user } = useApp();
    const { canPayCXP } = usePermissions();

    const [cuentas, setCuentas] = useState([]);
    const [filteredCuentas, setFilteredCuentas] = useState([]);
    const [proveedores, setProveedores] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterProveedor, setFilterProveedor] = useState('todos');
    const [selectedCuentas, setSelectedCuentas] = useState([]);
    const [showDetalleModal, setShowDetalleModal] = useState(false);
    const [ordenDetalle, setOrdenDetalle] = useState(null);
    const [loadingDetalle, setLoadingDetalle] = useState(false);

    // Estado para select con buscador de proveedor
    const [showProveedorDropdown, setShowProveedorDropdown] = useState(false);
    const [proveedorSearch, setProveedorSearch] = useState('');
    const proveedorDropdownRef = useRef(null);

    // Fechas por defecto: mes en curso
    const getDefaultDates = () => {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return {
            inicio: firstDay.toISOString().split('T')[0],
            fin: lastDay.toISOString().split('T')[0]
        };
    };

    const defaultDates = getDefaultDates();
    const [fechaInicio, setFechaInicio] = useState(defaultDates.inicio);
    const [fechaFin, setFechaFin] = useState(defaultDates.fin);

    useEffect(() => {
        loadCuentas();
        loadProveedores();
    }, []);

    useEffect(() => {
        filterCuentas();
    }, [cuentas, searchQuery, filterProveedor]);

    // Auto-filtrar cuando cambian las fechas
    useEffect(() => {
        const timer = setTimeout(() => {
            loadCuentas();
        }, 500);
        return () => clearTimeout(timer);
    }, [fechaInicio, fechaFin]);

    // Cerrar dropdown al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (proveedorDropdownRef.current && !proveedorDropdownRef.current.contains(event.target)) {
                setShowProveedorDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const loadCuentas = async () => {
        try {
            setLoading(true);
            const result = await apiService.getCuentasPorPagar({
                fecha_inicio: fechaInicio,
                fecha_fin: fechaFin
            });
            if (result.success) {
                setCuentas(result.data?.ordenes || []);
            } else {
                showToast(result.message, 'error');
            }
        } catch (error) {
            showToast('Error al cargar cuentas por pagar', 'error');
        } finally {
            setLoading(false);
        }
    };

    const loadProveedores = async () => {
        try {
            const result = await apiService.getProveedores();
            if (result.success) {
                setProveedores(result.data || []);
            }
        } catch (error) {
            console.error('Error al cargar proveedores:', error);
        }
    };

    const filterCuentas = () => {
        let filtered = cuentas;

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(cuenta =>
                cuenta.folio?.toLowerCase().includes(query) ||
                cuenta.proveedor_nombre?.toLowerCase().includes(query)
            );
        }

        if (filterProveedor !== 'todos') {
            filtered = filtered.filter(cuenta => cuenta.proveedor_id === parseInt(filterProveedor));
        }

        setFilteredCuentas(filtered);
    };

    const clearDateFilters = () => {
        const defaults = getDefaultDates();
        setFechaInicio(defaults.inicio);
        setFechaFin(defaults.fin);
    };

    // Filtrar proveedores para el buscador
    const filteredProveedores = proveedores.filter(p =>
        p.nombre?.toLowerCase().includes(proveedorSearch.toLowerCase())
    );

    // Obtener nombre del proveedor seleccionado
    const getProveedorNombre = () => {
        if (filterProveedor === 'todos') return 'Todos los proveedores';
        const prov = proveedores.find(p => p.id === parseInt(filterProveedor));
        return prov?.nombre || 'Seleccionar proveedor';
    };

    const handleSelectProveedor = (id) => {
        setFilterProveedor(id);
        setShowProveedorDropdown(false);
        setProveedorSearch('');
    };

    const handleSelectCuenta = (id) => {
        setSelectedCuentas(prev => {
            if (prev.includes(id)) {
                return prev.filter(i => i !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    const handleSelectAll = () => {
        if (selectedCuentas.length === filteredCuentas.length) {
            setSelectedCuentas([]);
        } else {
            setSelectedCuentas(filteredCuentas.map(c => c.id));
        }
    };

    const handleMarcarPagada = async (id) => {
        if (!window.confirm('¿Confirmas que esta orden ya fue pagada?')) {
            return;
        }

        try {
            setLoading(true);
            const result = await apiService.marcarComoPagada(id, user?.user_id || null);
            if (result.success) {
                showToast('Orden marcada como pagada', 'success');
                loadCuentas();
                setSelectedCuentas(prev => prev.filter(i => i !== id));
            } else {
                showToast(result.message, 'error');
            }
        } catch (error) {
            showToast('Error al marcar como pagada', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleMarcarSeleccionadas = async () => {
        if (selectedCuentas.length === 0) {
            showToast('Selecciona al menos una orden', 'warning');
            return;
        }

        if (!window.confirm(`¿Confirmas marcar ${selectedCuentas.length} orden(es) como pagada(s)?`)) {
            return;
        }

        try {
            setLoading(true);
            const result = await apiService.marcarVariasComoPagadas(selectedCuentas);
            if (result.success) {
                showToast(result.message, 'success');
                loadCuentas();
                setSelectedCuentas([]);
            } else {
                showToast(result.message, 'error');
            }
        } catch (error) {
            showToast('Error al marcar como pagadas', 'error');
        } finally {
            setLoading(false);
        }
    };

    const getDiasTranscurridos = (fecha) => {
        if (!fecha) return '-';
        const fechaRecepcion = new Date(fecha);
        const hoy = new Date();
        const diff = Math.floor((hoy - fechaRecepcion) / (1000 * 60 * 60 * 24));
        return diff;
    };

    const handleVerDetalle = async (id) => {
        try {
            setLoadingDetalle(true);
            setShowDetalleModal(true);
            const result = await apiService.getOrdenCompra(id);
            if (result.success) {
                setOrdenDetalle(result.data);
            } else {
                showToast('Error al cargar detalle de la orden', 'error');
                setShowDetalleModal(false);
            }
        } catch (error) {
            showToast('Error al cargar detalle de la orden', 'error');
            setShowDetalleModal(false);
        } finally {
            setLoadingDetalle(false);
        }
    };

    const handleCloseDetalle = () => {
        setShowDetalleModal(false);
        setOrdenDetalle(null);
    };

    return (
        <div className="cxp-page">
                    {/* Controles */}
                    <div className="cxp-controls">
                        <div className="search-filters">
                            <div className="search-container">
                                <Input
                                    placeholder="Buscar por folio o proveedor..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    icon={<Search size={20} />}
                                />
                            </div>
                            <div className="date-filters">
                                <div className="date-input-group">
                                    <label>Desde:</label>
                                    <input
                                        type="date"
                                        value={fechaInicio}
                                        onChange={(e) => setFechaInicio(e.target.value)}
                                        className="date-input"
                                    />
                                </div>
                                <div className="date-input-group">
                                    <label>Hasta:</label>
                                    <input
                                        type="date"
                                        value={fechaFin}
                                        onChange={(e) => setFechaFin(e.target.value)}
                                        className="date-input"
                                    />
                                </div>
                                <button
                                    className="clear-dates-btn"
                                    onClick={clearDateFilters}
                                    title="Limpiar fechas"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                            <div className="proveedor-filter" ref={proveedorDropdownRef}>
                                <button
                                    className="proveedor-select-btn"
                                    onClick={() => setShowProveedorDropdown(!showProveedorDropdown)}
                                >
                                    <Truck size={16} />
                                    <span>{getProveedorNombre()}</span>
                                    <ChevronDown size={16} className={showProveedorDropdown ? 'rotated' : ''} />
                                </button>
                                {showProveedorDropdown && (
                                    <div className="proveedor-dropdown">
                                        <div className="dropdown-search">
                                            <Search size={16} />
                                            <input
                                                type="text"
                                                placeholder="Buscar proveedor..."
                                                value={proveedorSearch}
                                                onChange={(e) => setProveedorSearch(e.target.value)}
                                                autoFocus
                                            />
                                        </div>
                                        <div className="dropdown-options">
                                            <div
                                                className={`dropdown-option ${filterProveedor === 'todos' ? 'selected' : ''}`}
                                                onClick={() => handleSelectProveedor('todos')}
                                            >
                                                Todos los proveedores
                                            </div>
                                            {filteredProveedores.map(p => (
                                                <div
                                                    key={p.id}
                                                    className={`dropdown-option ${filterProveedor === p.id.toString() ? 'selected' : ''}`}
                                                    onClick={() => handleSelectProveedor(p.id.toString())}
                                                >
                                                    {p.nombre}
                                                </div>
                                            ))}
                                            {filteredProveedores.length === 0 && proveedorSearch && (
                                                <div className="dropdown-empty">
                                                    No se encontraron proveedores
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="controls-actions">
                            {canPayCXP() && selectedCuentas.length > 0 && (
                                <Button onClick={handleMarcarSeleccionadas} disabled={loading}>
                                    <Check size={18} />
                                    Marcar {selectedCuentas.length} como Pagada(s)
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Tabla */}
                    <div className="cxp-table-container">
                        {loading && (
                            <div className="loading-state">
                                <div className="loading-spinner"></div>
                                <p>Cargando cuentas por pagar...</p>
                            </div>
                        )}

                        {!loading && filteredCuentas.length === 0 && (
                            <div className="empty-state">

                                <h3>No hay cuentas pendientes</h3>
                                <p>No hay órdenes pendientes de pago en este período</p>
                            </div>
                        )}

                        {!loading && filteredCuentas.length > 0 && (
                            <div className="cxp-table-wrapper">
                                <table className="cxp-table">
                                    <thead>
                                        <tr>
                                            {canPayCXP() && (
                                                <th className="checkbox-col">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedCuentas.length === filteredCuentas.length && filteredCuentas.length > 0}
                                                        onChange={handleSelectAll}
                                                    />
                                                </th>
                                            )}
                                            <th>Folio</th>
                                            <th>Fecha Recepción</th>
                                            <th>Proveedor</th>
                                            <th>Días</th>
                                            <th className="text-right">Total</th>
                                            {canPayCXP() && <th>Acciones</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredCuentas.map((cuenta) => {
                                            const dias = getDiasTranscurridos(cuenta.fecha_recepcion);
                                            const esUrgente = dias > 30;

                                            return (
                                                <tr key={cuenta.id} className={esUrgente ? 'urgente' : ''}>
                                                    {canPayCXP() && (
                                                        <td className="checkbox-col">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedCuentas.includes(cuenta.id)}
                                                                onChange={() => handleSelectCuenta(cuenta.id)}
                                                            />
                                                        </td>
                                                    )}
                                                    <td>
                                                        <div
                                                            className="orden-folio clickable"
                                                            onClick={() => handleVerDetalle(cuenta.id)}
                                                            title="Ver detalle"
                                                        >
                                                            <Package size={16} />
                                                            <span>{cuenta.folio}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="orden-fecha">
                                                            <Calendar size={14} />
                                                            <span>{formatDate(cuenta.fecha_recepcion)}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="orden-proveedor">
                                                            <span>{cuenta.proveedor_nombre}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className={`dias-badge ${esUrgente ? 'urgente' : dias > 15 ? 'warning' : ''}`}>
                                                            {dias} días
                                                        </span>
                                                    </td>
                                                    <td className="text-right">
                                                        <span className="orden-total">{formatMoney(cuenta.total)}</span>
                                                    </td>
                                                    {canPayCXP() && (
                                                        <td>
                                                            <button
                                                                className="action-btn-pagar"
                                                                onClick={() => handleMarcarPagada(cuenta.id)}
                                                                title="Marcar como pagada"
                                                                disabled={loading}
                                                            >
                                                                <Check size={14} />
                                                                Pagar
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan={canPayCXP() ? 6 : 5} className="text-right">
                                                <strong>Total:</strong>
                                            </td>
                                            <td className="text-right">
                                                <strong className="total-footer">
                                                    {formatMoney(filteredCuentas.reduce((sum, c) => sum + parseFloat(c.total), 0))}
                                                </strong>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}
                    </div>

            {/* Modal de Detalle de Orden */}
            {showDetalleModal && (
                <Modal
                    title={`Detalle de Orden - ${ordenDetalle?.folio || ''}`}
                    onClose={handleCloseDetalle}
                    size="large"
                >
                    <OrdenCompraDetalle
                        orden={ordenDetalle}
                        loading={loadingDetalle}
                        onClose={handleCloseDetalle}
                        actions={ordenDetalle && canPayCXP() ? [{
                            label: 'Marcar como Pagada',
                            icon: Check,
                            onClick: () => {
                                handleMarcarPagada(ordenDetalle.id);
                                handleCloseDetalle();
                            }
                        }] : []}
                    />
                </Modal>
            )}
        </div>
    );
};

export default CuentasPorPagarPage;
